import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import {
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
} from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon';
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider';
import React from 'react';
import * as SX from './sx';
import type { CustomTreeItemContentProps } from './types';
import { TreeItemIconBlock } from './tree-item-icon-block';
import { TreeItemLabelBlock } from './tree-item-label-block';
import { TreeItemMetadataBlock } from './tree-item-metadata-block';
import { TreeItemMetadataCount } from './tree-item-metadata-count';

const handlePreventDefault = (e: React.MouseEvent) => {
  e.preventDefault();
};

const EXPAND_ICON = <Icon name="arrow-tree-right" size={14} />;
const COLLAPSE_ICON = <Icon name="arrow-tree-down" size={14} />;
const TREE_ICON_SLOTS = {
  expandIcon: () => EXPAND_ICON,
  collapseIcon: () => COLLAPSE_ICON,
};

function CustomTreeItemContentComponent({
  checked,
  indeterminate,
  item,
  itemId,
  getRootProps,
  getContentProps,
  getCheckboxProps,
  getLabelProps,
  getGroupTransitionProps,
  getIconContainerProps,
  status,
  handleItemClick,
  handleCheckboxClick,
  checkboxPosition,
  multiSelect: _multiSelect,
  metadataApiLoading,
  metadataEnabled,
  iconSx,
  avartarFallbackIcon,
  children,
  other,
  contentSx,
  hasChildrenOrParent: _hasChildrenOrParent,
  hasAnyNephews,
  className,
}: CustomTreeItemContentProps) {
  return (
    <Box className={classnames('CustomTreeItem', className)} sx={SX.root}>
      <TreeItem2Provider itemId={itemId}>
        <TreeItem2Root {...getRootProps(other)} onClick={handleItemClick}>
          <TreeItem2Content
            {...getContentProps()}
            status={status}
            sx={contentSx}
            onMouseDown={handlePreventDefault}
          >
            {!!item.parentId && <Box sx={SX.forkBorder} />}
            {(!!children || hasAnyNephews) && (
              <TreeItem2IconContainer {...getIconContainerProps()}>
                <TreeItem2Icon status={status} slots={TREE_ICON_SLOTS} />
              </TreeItem2IconContainer>
            )}
            {checkboxPosition === 'left' && (
              <TreeItem2Checkbox
                {...({
                  ...getCheckboxProps(),
                  checked,
                  size: 'small',
                  onChange: handleCheckboxClick,
                  indeterminate,
                  sx: SX.checkboxLeftSx,
                  disabled: item.disabled,
                } as React.ComponentProps<typeof TreeItem2Checkbox>)}
              />
            )}
            <TreeItemIconBlock
              icon={
                typeof item.metadata?.icon === 'string'
                  ? item.metadata.icon
                  : undefined
              }
              logoUrl={item.metadata?.logo_url}
              iconSx={iconSx}
              avartarFallbackIcon={avartarFallbackIcon}
            />

            <TreeItem2Label
              {...getLabelProps()}
              children={
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <TreeItemLabelBlock item={item} />
                  <TreeItemMetadataBlock item={item} />
                </Box>
              }
            />
            <TreeItemMetadataCount
              item={item}
              metadataEnabled={metadataEnabled}
              metadataApiLoading={metadataApiLoading}
            />
            {!item.metadata?.comingSoon && checkboxPosition === 'right' && (
              <TreeItem2Checkbox
                {...({
                  ...getCheckboxProps(),
                  checked,
                  size: 'small',
                  onChange: handleCheckboxClick,
                  indeterminate,
                  disabled: item.disabled,
                } as React.ComponentProps<typeof TreeItem2Checkbox>)}
              />
            )}
          </TreeItem2Content>
          {children && (
            <TreeItem2GroupTransition {...getGroupTransitionProps()} />
          )}
        </TreeItem2Root>
      </TreeItem2Provider>
    </Box>
  );
}

CustomTreeItemContentComponent.displayName = 'CustomTreeItemContent';

export const CustomTreeItemContent = React.memo(CustomTreeItemContentComponent);
